/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import React, { useMemo } from "react";

import { useDashboardStore } from "@/stores/useDashboardStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import CircularProgress from "@mui/material/CircularProgress";
import MuiRadio, { RadioProps as MuiRadioProps } from "@mui/material/Radio";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import { CompaniesDto, ROUTES } from "../../shared";
import { useOverflow } from "../../shared/hooks/useOverflow";
import useRadioIcons from "../../shared/hooks/useRadioIcons";
import OptionalTooltip from "../Tooltip/OptionalTooltip";
import { useStyles } from "./styles";

export interface IRadioListItemProps {
  route: ROUTES;
  label?: string;
  info?: string;
  isError?: boolean;
  selectedValue?: string | CompaniesDto;
}

const { blue100, blue200, blue700, blue800, gray300, gray400, textDark } =
  colors;

const labelBaseStyle = {
  color: textDark,
  "&:hover": {
    backgroundColor: blue100
  },

  "&:active": {
    backgroundColor: blue200
  }
};

const textBaseStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
} as const;

const dimmedTextStyle = css({ ...textBaseStyle, color: gray300 });
const inheritedTextStyle = css({ ...textBaseStyle, color: "inherit" });

const RadioListItem = ({
  route,
  label,
  info,
  isError,
  selectedValue,
  ...props
}: IRadioListItemProps & MuiRadioProps) => {
  const { getIcons } = useRadioIcons();

  const { measuredRef, isOverflowing } = useOverflow([]);
  const { radioListItemLabel, radioListItemContentHolder, radioLoaderDiv } =
    useStyles();

  const isDashboardLoading = useDashboardStore(
    state => state.isDashboardLoading
  );
  // Select just `loading`: the old whole-store subscription re-rendered every
  // item in the list on any table store change
  const isTableLoading = useTableStoreByRoute(route, state => state.loading);

  const { icon, checkedIcon } = getIcons(props, isError);

  const isDefault = !props.disabled && !isError;

  const isSelectedItem = selectedValue === props.value;

  const isItemLoading =
    route === ROUTES.Dashboard ? isDashboardLoading : isTableLoading;

  const labelStyle = useMemo(
    () => css([labelBaseStyle, radioListItemLabel]),
    [radioListItemLabel]
  );

  const radioStyle = useMemo(
    () => ({
      padding: "0",
      marginRight: label || info ? "8px" : "0",
      color: gray300,

      "& > svg": {
        fontSize: "24px"
      },

      "&:hover": {
        color: isDefault ? gray400 : "unset"
      },
      "&.Mui-checked": {
        color: blue700
      },
      "&:hover.Mui-checked": {
        color: isDefault ? blue800 : "unset"
      }
    }),
    [label, info, isDefault]
  );

  return (
    <label css={labelStyle}>
      <div css={radioListItemContentHolder}>
        <OptionalTooltip
          tooltipPlacement="top"
          tooltipTitle={label ?? ""}
          isDisabled={!isOverflowing}
        >
          <Typography
            variant="body2Medium"
            css={
              isItemLoading && !isSelectedItem
                ? dimmedTextStyle
                : inheritedTextStyle
            }
            ref={measuredRef}
          >
            {label}
          </Typography>
        </OptionalTooltip>

        {isItemLoading && isSelectedItem ? (
          <div css={radioLoaderDiv}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <MuiRadio
            data-testid="radio"
            {...props}
            size="small"
            icon={icon}
            checkedIcon={checkedIcon}
            disabled={isItemLoading}
            sx={radioStyle}
          />
        )}
      </div>
    </label>
  );
};

export default React.memo(RadioListItem);
